import BaseStore from './baseStore'
export const namespaced = true
import generateBaseImportFromStage from '@/store/baseImportFromStage'
import { generateMultipleItemOverview } from '@/store/baseOverview'
import { getNewId } from '@/utils/id'
import api from '@/api/api'

const baseUrl = '/soil_lines/'

const DEFAULT_ADD_VALUE = 0.5

const baseStore = new BaseStore(baseUrl)
const baseOverview = generateMultipleItemOverview({
  itemFilter: (soilLine, stabSchematisationStage) =>
    soilLine.soil_composition === stabSchematisationStage.soilcomposition,
  duplicationCheckFunc: (soilLine, soilCompositionId) =>
    soilLine.soil_composition === soilCompositionId
})
const baseImportFromStage = generateBaseImportFromStage({})

const getDefaultState = () => {
  return {
    ...structuredClone(baseStore.state),
    tableHeaders: []
  }
}

export const state = {
  ...getDefaultState(),
  ...structuredClone(baseOverview.state),
  ...structuredClone(baseImportFromStage.state)
}

export const mutations = {
  ...baseStore.mutations,
  ...baseOverview.mutations,
  ...baseImportFromStage.mutations,
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  INCREASE_SOIL_LINE_NUMBER(state, item) {
    item.number = item.number + 1
  },
  DECREASE_SOIL_LINE_NUMBER(state, item) {
    item.number = item.number - 1
  },
  DELETE_X_COORDINATE(state, { item, index }) {
    item.geometry.coordinates.splice(index, 1)
  },
  SPLICE_NEW_COORDINATE(state, { item, index, coordinate }) {
    item.geometry.coordinates.splice(index, 0, coordinate)
  },
  PUSH_NEW_COORDINATE(state, { item, coordinate }) {
    item.geometry.coordinates.push(coordinate)
  },
  ADD_BEFORE_X_COORDINATE(state, { item, newXCoordinate, index }) {
    const stateItem = state.items.find((i) => i.id === item.id)
    const yCoordinate = index == null ? 999 : item.geometry.coordinates[index][1]

    const refIndex = index ?? 0
    if (index == null) {
      stateItem.geometry.coordinates.splice(refIndex, 0, [newXCoordinate, yCoordinate])
    }
    stateItem.geometry.coordinates.splice(refIndex, 0, [newXCoordinate, yCoordinate])
  },
  ADD_AFTER_X_COORDINATE(state, { item, newXCoordinate, refIndex }) {
    if (refIndex == null) {
      const lastIndexOfCoordinates = item.geometry.coordinates.length - 1
      const yCoordinate = item.geometry.coordinates[lastIndexOfCoordinates][1]
      item.geometry.coordinates.push([newXCoordinate, yCoordinate])
    } else {
      const yCoordinate = item.geometry.coordinates[refIndex][1]
      item.geometry.coordinates.splice(refIndex + 1, 0, [newXCoordinate, yCoordinate])
    }
  },
  SET_TABLE_HEADERS(state, headers) {
    state.tableHeaders = headers
  },
  SORT_SOIL_LINES(state) {
    state.items.sort((a, b) => a.number - b.number)
  }
}

export const actions = {
  ...baseStore.actions,
  ...baseOverview.actions,
  ...baseImportFromStage.actions,
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  async fetchItems(context, id) {
    await baseStore.actions.fetchItems.call(this, context, id)
    context.commit('SORT_SOIL_LINES')
  },
  // handle the add new soil line
  // with selectedItem parameter is the selected soil line
  // and insertAfter parameter is the flag to insert the new soil line after the selected soil line
  addSoilLine(context, { selectedItem = null, insertAfter = false }) {
    let soilLineNumber = 1
    let selectedNumber = 0
    if (selectedItem !== null) {
      soilLineNumber = insertAfter ? selectedItem.number + 1 : selectedItem.number
      selectedNumber = selectedItem.number
    }
    const selectedSoilLine = context.state.items.find(
      (soilLine) => soilLine.number === selectedNumber
    )
    // get the coordinates of the new soil line
    let coordinates = []
    if (selectedSoilLine) {
      coordinates = selectedSoilLine.geometry.coordinates.map((coordinate) => {
        return [...coordinate]
      })
    } else {
      coordinates = context.getters.getXColumns().map((xCoordinate) => {
        return [xCoordinate, 0]
      })
    }
    // increase the number of the soil lines which have the number greater than the new soil line
    const soilLines = context.state.items
    soilLines.forEach((soilLine) => {
      if (soilLine.number >= soilLineNumber) {
        context.commit('INCREASE_SOIL_LINE_NUMBER', soilLine)
        context.commit('ADD_DIRTY_ITEM', soilLine)
      }
    })
    // add the new soil line to the store
    let newId = getNewId(context.state.items, 'id')
    const compositionId = context.rootGetters['stabSchematisationStage/currentCompositionId']
    let newItem = context.getters.getEmptyItem(compositionId, soilLineNumber, coordinates)
    newItem['id'] = parseInt(newId)
    context.commit('ADD_ITEM', newItem)
    context.commit('SORT_SOIL_LINES')
  },
  async deleteSoilLine(context, item) {
    // delete the soil line from database
    if (item.id > 0) {
      let url = context.state.baseUrl + `${item.id}/`
      await api.delete(url)
    }
    // delete the soil line from the store
    context.commit('DELETE_ITEM', item.id)
    // decrease the number of the soil lines which have the number greater than the deleted soil line
    const soilLines = context.state.items
    soilLines.forEach((soilLine) => {
      if (soilLine.number >= item.number) {
        context.commit('DECREASE_SOIL_LINE_NUMBER', soilLine)
        context.commit('ADD_DIRTY_ITEM', soilLine)
      }
    })
    context.commit('SORT_SOIL_LINES')
  },
  // handle the edit x coordinate of soil profile
  editXCoordinate(context, { index, xCoordinate }) {
    xCoordinate = parseFloat(xCoordinate)
    const soilLines = context.state.items
    soilLines.forEach((item) => {
      const yCoordinate = item.geometry.coordinates[index][1]
      context.commit('DELETE_X_COORDINATE', { item, index })
      context.commit('ADD_DIRTY_ITEM', item)
      const newIndex = item.geometry.coordinates.findIndex(
        (coordinate) => xCoordinate < coordinate[0]
      )
      if (newIndex === -1) {
        context.commit('PUSH_NEW_COORDINATE', { item, coordinate: [xCoordinate, yCoordinate] })
      } else {
        context.commit('SPLICE_NEW_COORDINATE', {
          item,
          index: newIndex,
          coordinate: [xCoordinate, yCoordinate]
        })
      }
    })
    context.commit('SET_TABLE_HEADERS', context.getters.getXColumns())
  },
  // handle the add new x coordinate to the left of the selected x coordinate
  addNewXCoordinateBefore(context, index) {
    const xColumns = context.getters.getXColumns()
    let newXCoordinate = 0
    let refIndex = null
    if (xColumns.length > 0) {
      let xCoordinate = xColumns[index]
      refIndex = index
      newXCoordinate = xCoordinate - DEFAULT_ADD_VALUE
      if (index - 1 >= 0) {
        let refXCoordinate = xColumns[index - 1]
        newXCoordinate = (xCoordinate + refXCoordinate) / 2
      }
    }
    const soilLines = context.state.items
    soilLines.forEach((item) => {
      context.commit('ADD_BEFORE_X_COORDINATE', { item, newXCoordinate, index: refIndex })
      context.commit('ADD_DIRTY_ITEM', item)
    })
    context.commit('SET_TABLE_HEADERS', context.getters.getXColumns())
  },
  // handle the add new x coordinate to the right of the selected x coordinate
  addNewXCoordinateAfter(context, index) {
    const xColumns = context.getters.getXColumns()
    let newXCoordinate = 0
    let refIndex = null
    if (xColumns.length > 0) {
      let xCoordinate = xColumns[index]
      refIndex = index + 1
      newXCoordinate = xCoordinate + DEFAULT_ADD_VALUE
      if (refIndex >= xColumns.length) {
        const soilLines = context.state.items
        soilLines.forEach((item) => {
          context.commit('ADD_AFTER_X_COORDINATE', { item, newXCoordinate, refIndex: null })
          context.commit('ADD_DIRTY_ITEM', item)
        })
      } else {
        let refXCoordinate = xColumns[refIndex]
        newXCoordinate = (xCoordinate + refXCoordinate) / 2
        const soilLines = context.state.items
        soilLines.forEach((item) => {
          context.commit('ADD_AFTER_X_COORDINATE', { item, newXCoordinate, refIndex: index })
          context.commit('ADD_DIRTY_ITEM', item)
        })
      }
    }
    context.commit('SET_TABLE_HEADERS', context.getters.getXColumns())
  },
  setTableHeaders(context) {
    context.commit('SET_TABLE_HEADERS', context.getters.getXColumns())
  },
  deleteXCoordinate(context, index) {
    const soilLines = context.state.items
    soilLines.forEach((item) => {
      context.commit('DELETE_X_COORDINATE', { item, index })
      context.commit('ADD_DIRTY_ITEM', item)
    })
    context.commit('SET_TABLE_HEADERS', context.getters.getXColumns())
  },
  async importSoilLinesFromStage(
    { commit, dispatch },
    { sourceSchematisationStageId, targetSchematisationStageId, soilCompositionId }
  ) {
    try {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
      const { imported_soil_lines } = await dispatch('importFromStage', {
        import_option: {
          source_stab_schematisation_stage_id: sourceSchematisationStageId,
          target_stab_schematisation_stage_id: targetSchematisationStageId
        },
        soil_composition: soilCompositionId
      })
      commit('SET_ITEMS', imported_soil_lines)
    } catch (e) {
      console.log(e)
    } finally {
      commit('TOGGLE_IMPORT_FROM_STAGE_LOADING')
    }
  }
}

export const getters = {
  ...baseOverview.getters,
  getEmptyItem: () => (compositionId, number, coordinates) => {
    return {
      id: 0,
      soil_composition: compositionId,
      geometry: { type: 'LineString', coordinates: coordinates },
      number: number,
      soil_type: null
    }
  },
  // Return a function to make sure we always get the latest state
  getXColumns: (state) => () => {
    return state.items.length
      ? state.items[0].geometry.coordinates.map((coordinate) => {
          return coordinate[0]
        })
      : []
  }
}
